import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Snackbar,
  Alert,
  Tooltip,
  Chip
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Download as DownloadIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import CreateQuote from './CreateQuote';
import config from '../../config/config';
import ConvertToInvoiceModal from './ConvertToInvoiceModal';

interface Quote {
  id: number;
  customerName: string;
  customerEmail: string;
  dueDate: string;
  status: 'pending' | 'accepted' | 'rejected' | 'invoiced';
  totalAmount: number;
  totalAmountWithVat?: number;
  pdfPath?: string;
}

const Quotes: React.FC = () => {
  const [quotes, setQuotes] = useState<Quote[]>([]);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error' | 'info'
  });
  const [convertModalOpen, setConvertModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState<Quote | null>(null);

  const fetchQuotes = async () => {
    try {
      const response = await fetch(`${config.api.baseUrl}/quotes`);
      if (!response.ok) {
        throw new Error('Kunde inte hämta offerter');
      }
      const data = await response.json();
      setQuotes(data);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ett fel uppstod när offerterna skulle hämtas',
        severity: 'error'
      });
    }
  };

  useEffect(() => {
    fetchQuotes();
  }, []);

  const handleDelete = async (id: number) => {
    if (window.confirm('Är du säker på att du vill ta bort denna offert?')) {
      try {
        const response = await fetch(`${config.api.baseUrl}/quotes/${id}`, {
          method: 'DELETE'
        });

        if (!response.ok) {
          throw new Error('Kunde inte ta bort offerten');
        }

        setQuotes(quotes.filter(quote => quote.id !== id));
        setSnackbar({
          open: true,
          message: 'Offerten togs bort framgångsrikt',
          severity: 'success'
        });
      } catch (error) {
        setSnackbar({
          open: true,
          message: 'Ett fel uppstod när offerten skulle tas bort',
          severity: 'error'
        });
      }
    }
  };

  const handleDownload = async (id: number) => {
    try {
      const response = await fetch(`${config.api.baseUrl}/quotes/${id}/pdf`);
      if (!response.ok) {
        throw new Error('Kunde inte ladda ner PDF:en');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `offert-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ett fel uppstod när PDF:en skulle laddas ner',
        severity: 'error'
      });
    }
  };

  const handleStatusChange = async (id: number, newStatus: Quote['status']) => {
    try {
      const response = await fetch(`${config.api.baseUrl}/quotes/${id}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ status: newStatus })
      });

      if (!response.ok) {
        throw new Error('Kunde inte uppdatera status');
      }

      setQuotes(quotes.map(quote =>
        quote.id === id ? { ...quote, status: newStatus } : quote
      ));

      setSnackbar({
        open: true,
        message: 'Status uppdaterades framgångsrikt',
        severity: 'success'
      });
    } catch (error) {
      setSnackbar({
        open: true,
        message: 'Ett fel uppstod när status skulle uppdateras',
        severity: 'error'
      });
    }
  };

  const handleConvertToInvoice = (quote: Quote) => {
    setSelectedQuote(quote);
    setConvertModalOpen(true);
  };

  const getStatusColor = (status: Quote['status']) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'success';
      case 'rejected':
        return 'error';
      case 'invoiced':
        return 'info';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: Quote['status']) => {
    switch (status) {
      case 'pending':
        return 'Väntande';
      case 'accepted':
        return 'Accepterad';
      case 'rejected':
        return 'Avvisad';
      case 'invoiced':
        return 'Fakturerad';
      default:
        return status;
    }
  };

  const handleQuoteCreated = (message: string, severity: 'success' | 'error' | 'info' = 'success') => {
    setSnackbar({ open: true, message, severity });
    fetchQuotes();
  };

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Offertförvaltning
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => setCreateDialogOpen(true)}
        >
          Ny offert
        </Button>
      </Box>

      <Card>
        <CardContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Kund</TableCell>
                  <TableCell>E-post</TableCell>
                  <TableCell>Förfallodatum</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Belopp</TableCell>
                  <TableCell>Åtgärder</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {quotes.map((quote) => (
                  <TableRow key={quote.id}>
                    <TableCell>{quote.customerName || '-'}</TableCell>
                    <TableCell>{quote.customerEmail || '-'}</TableCell>
                    <TableCell>{quote.dueDate ? new Date(quote.dueDate).toLocaleDateString('sv-SE') : '-'}</TableCell>
                    <TableCell>
                      <Chip
                        label={getStatusLabel(quote.status)}
                        color={getStatusColor(quote.status)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{typeof quote.totalAmountWithVat === 'number' ? quote.totalAmountWithVat.toFixed(2) + ' kr' : '-'}</TableCell>
                    <TableCell>
                      <Tooltip title="Ladda ner PDF">
                        <IconButton onClick={() => handleDownload(quote.id)}>
                          <DownloadIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Konvertera till faktura">
                        <span>
                          <IconButton
                            onClick={() => handleConvertToInvoice(quote)}
                            color="primary"
                            disabled={quote.status !== 'accepted'}
                          >
                            <ReceiptIcon />
                          </IconButton>
                        </span>
                      </Tooltip>
                      {quote.status === 'pending' && (
                        <>
                          <Tooltip title="Acceptera">
                            <IconButton
                              onClick={() => handleStatusChange(quote.id, 'accepted')}
                              color="success"
                            >
                              <CheckIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Avvisa">
                            <IconButton
                              onClick={() => handleStatusChange(quote.id, 'rejected')}
                              color="error"
                            >
                              <CloseIcon />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Ta bort">
                        <IconButton onClick={() => handleDelete(quote.id)} color="error">
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      <Dialog
        open={createDialogOpen}
        onClose={() => setCreateDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>Skapa ny offert</DialogTitle>
        <DialogContent>
          <CreateQuote onClose={() => setCreateDialogOpen(false)} onCreated={handleQuoteCreated} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Stäng</Button>
        </DialogActions>
      </Dialog>

      <ConvertToInvoiceModal
        open={convertModalOpen}
        onClose={() => setConvertModalOpen(false)}
        quote={selectedQuote}
        onSuccess={() => {
          setConvertModalOpen(false);
          setSnackbar({ open: true, message: 'Faktura skapad!', severity: 'success' });
          fetchQuotes();
        }}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Quotes; 